import DashLayout from "@/layouts/DashLayout";
import RoleProtectedPage from "@/layouts/RoleProtectedPage";
import { POS_PERMISSIONS } from "@/lib/navigation";
import { useAppSelector } from "@/redux/features/hooks";
import { Roles } from "@/redux/services/user/user.type";
import { useGetActiveMenuQuery } from "@/redux/services/menu/menu.api";
import { useGetMenuCombosQuery } from "@/redux/services/menu/menu_combo.api";
import { useGetActiveMenuFormulasQuery } from "@/redux/services/menu/menu_formula.api";
import { useGetActiveRestaurantDailyDishesQuery } from "@/redux/services/dailyDishes/dailyDishes.api";
import {
  useCreatePosOrderMutation,
  useGetOrderByIdQuery,
  useUpdateOrderMutation,
} from "@/redux/services/order/order.api";
import { useGetRestaurantTablesQuery } from "@/redux/services/table/table.api";
import { MenuItem, MenuItemOptionValue } from "@/redux/services/menu/menu.type";
import { MenuCombo } from "@/redux/services/menu/menu_combo.type";
import { MenuFormula } from "@/redux/services/menu/menu_formula.type";
import { DailyDish } from "@/redux/services/dailyDishes/dailyDishes.type";
import { CreatePosOrderRequest } from "@/redux/services/pos/pos.type";
import {
  cn,
  displayPrice,
  formatSideType,
  getImageUrl,
  isWithinEditWindow,
} from "@/lib/utils";
import { useRestaurantCurrency } from "@/lib/hooks";
import {
  ChefHat,
  ClipboardList,
  Info,
  LayoutGrid,
  Minus,
  Plus,
  Search,
  ShoppingCart,
  Trash2,
  UtensilsCrossed,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { Button } from "@/components/ui/button";
import PosItemCard from "@/components/pos/terminal/PosItemCard";
import CartPanel from "@/components/pos/terminal/CartPanel";
import ItemOptionSheet from "@/components/pos/terminal/ItemOptionSheet";
import SimpleItemModal from "@/components/pos/terminal/SimpleItemModal";
import {
  comboSelectionKey,
  comboSelectionLabel,
  indexMenuItems,
} from "@/lib/comboOptions";
import ComboConfigModal, {
  ComboSelection,
} from "@/components/pos/terminal/ComboConfigModal";
import FormulaConfigModal, {
  FormulaSelection,
} from "@/components/pos/terminal/FormulaConfigModal";
import NewOrderModal, {
  NewOrderConfig,
} from "@/components/pos/terminal/NewOrderModal";
import ConfirmOrderModal, {
  ConfirmOrderDetails,
} from "@/components/pos/terminal/ConfirmOrderModal";
import {
  CartEntry,
  DraftOrder,
  FormulaCartSelection,
  ORDER_TYPE_LABELS,
  buildCartEntryId,
  createDraftOrderId,
} from "@/components/pos/terminal/types";
import {
  loadDrafts,
  saveDrafts,
} from "@/components/pos/terminal/draftPersistence";
import {
  cartFromOrder,
  notesFromOrder,
} from "@/components/pos/terminal/cartFromOrder";
import {
  buildOrderItemsPayload,
  buildUpdatePayload,
} from "@/components/pos/terminal/orderPayloads";
import { translateOrderUpdateError } from "@/components/pos/terminal/orderEditErrors";
import OrderDetailsDrawer from "@/components/order/OrderDetailsDrawer";
import StockWarningsBanner from "@/components/inventory/StockWarningsBanner";
import { StockWarning } from "@/redux/services/inventory/inventory.type";
import { useGetOpenSessionQuery } from "@/redux/services/pos/registerSession.api";
import { Banknote } from "lucide-react";
import { useConfirm } from "@/components/ui/confirm-dialog";
import PosActiveOrders from "@/components/pos/terminal/PosActiveOrders";

// Special section IDs for combos, daily dishes, and formulas
const SECTION_COMBOS = "__combos__";
const SECTION_DAILY_DISHES = "__daily_dishes__";
const SECTION_FORMULAS = "__formulas__";

// Collect order-create metadata (delivery person + dine-in guest count) into a
// single object, or undefined when there's nothing to send.
function buildCreateMetadata(
  details: ConfirmOrderDetails
): Record<string, unknown> | undefined {
  const metadata: Record<string, unknown> = {};
  if (details.deliveryPersonName)
    metadata.deliveryPersonName = details.deliveryPersonName;
  if (details.deliveryPersonPhone)
    metadata.deliveryPersonPhone = details.deliveryPersonPhone;
  if (typeof details.guestCount === "number")
    metadata.guestCount = details.guestCount;
  return Object.keys(metadata).length > 0 ? metadata : undefined;
}

const PosPage = () => {
  const restaurant = useAppSelector((state) => state.user.currentRestaurant);
  const currentUser = useAppSelector((state) => state.user.currentUser);
  // Dine-in guest count is collected only when the restaurant opts in.
  const askGuestCount = !!restaurant?.settings?.shouldAskForGuestCount;
  const currency = useRestaurantCurrency();
  const confirm = useConfirm();
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(
    null
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isNewOrderModalOpen, setIsNewOrderModalOpen] = useState(false);

  // Multi-order state
  const [draftOrders, setDraftOrders] = useState<DraftOrder[]>([]);
  const [activeOrderId, setActiveOrderId] = useState<string | null>(null);
  const draftsHydratedRef = useRef(false);

  // Item option modal state
  const [optionSheetItem, setOptionSheetItem] = useState<MenuItem | null>(null);
  const [isOptionSheetOpen, setIsOptionSheetOpen] = useState(false);

  // Simple item modal state (for daily dishes with notes)
  const [simpleItemModal, setSimpleItemModal] = useState<{
    id: string;
    type: "combo" | "daily_dish";
    name: string;
    price: number;
    imageUrl?: string;
  } | null>(null);

  // Combo config modal state
  const [comboConfigItem, setComboConfigItem] = useState<MenuCombo | null>(
    null
  );

  // Formula config modal state
  const [formulaConfigItem, setFormulaConfigItem] =
    useState<MenuFormula | null>(null);

  const [createPosOrder] = useCreatePosOrderMutation();
  const [updateOrder] = useUpdateOrderMutation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [stockWarnings, setStockWarnings] = useState<StockWarning[]>([]);

  // ---------- Edit mode ----------
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const editOrderId = searchParams.get("editOrder");
  const showOrderId = searchParams.get("showOrder");
  const isEditMode = !!editOrderId;
  const editingOrderHydratedRef = useRef<string | null>(null);

  const { data: editingOrder, isFetching: isLoadingEditOrder } =
    useGetOrderByIdQuery(
      { id: editOrderId ?? "" },
      { skip: !editOrderId }
    );

  // On a committed order (ready/served) only add-ons are allowed: existing
  // lines are locked (can't be removed or reduced), new lines can be added.
  const editAddOnly =
    isEditMode &&
    !!editingOrder &&
    !editingOrder.canBeModified &&
    !!editingOrder.canAddItems;

  const isWaiter = currentUser?.roles?.includes(Roles.waiter) ?? false;

  const { data: openSessionData, isLoading: isLoadingSessions } =
    useGetOpenSessionQuery(
      { restaurantId: restaurant?.id ?? "" },
      { skip: !restaurant?.id }
    );

  const hasOpenSession = !!openSessionData;

  const { data: activeMenu, isLoading } = useGetActiveMenuQuery(
    restaurant?.id ?? "",
    { skip: !restaurant?.id }
  );

  const { data: tablesData } = useGetRestaurantTablesQuery(
    { restaurantId: restaurant?.id },
    { skip: !restaurant?.id }
  );

  const tables = tablesData?.results ?? [];

  const { data: combosData } = useGetMenuCombosQuery(
    { menuId: activeMenu?.id ?? "" },
    { skip: !activeMenu?.id }
  );

  const { data: formulasData } = useGetActiveMenuFormulasQuery(
    restaurant?.id ?? "",
    { skip: !restaurant?.id }
  );

  const { data: dailyDishesData } = useGetActiveRestaurantDailyDishesQuery(
    restaurant?.id ?? "",
    { skip: !restaurant?.id }
  );

  const combos = combosData?.results ?? [];
  const menuItemsById = useMemo(() => indexMenuItems(activeMenu), [activeMenu]);
  const formulas = formulasData ?? [];
  const dailyDishes = dailyDishesData ?? [];

  const activeOrder = draftOrders.find((o) => o.id === activeOrderId) ?? null;
  const cart = activeOrder?.cart ?? [];

  const restaurantId = restaurant?.id;

  useEffect(() => {
    if (!restaurantId || draftsHydratedRef.current) return;
    // In edit mode the draft list is rebuilt from the order being edited, not
    // from previously persisted POS drafts.
    if (isEditMode) {
      draftsHydratedRef.current = true;
      return;
    }
    const persisted = loadDrafts(restaurantId);
    if (persisted) {
      setDraftOrders(persisted.drafts);
      setActiveOrderId(persisted.activeOrderId);
    }
    draftsHydratedRef.current = true;
  }, [restaurantId, isEditMode]);

  useEffect(() => {
    if (!restaurantId || !draftsHydratedRef.current) return;
    // Don't persist edit-mode drafts — they're transient and tied to a
    // server-side order. Filter them out defensively in case one is still
    // in state during the transition out of edit mode.
    if (isEditMode) return;
    const persistable = draftOrders.filter((o) => !o.editingOrderId);
    saveDrafts(restaurantId, persistable, activeOrderId);
  }, [restaurantId, draftOrders, activeOrderId, isEditMode]);

  // Reset the hydration sentinel whenever edit mode is left, so re-entering
  // edit (even for the same order) always re-hydrates a fresh draft.
  useEffect(() => {
    if (!isEditMode) {
      editingOrderHydratedRef.current = null;
    }
  }, [isEditMode]);

  // Hydrate the cart from the order being edited (once both are loaded).
  useEffect(() => {
    if (!isEditMode || !editingOrder || !activeMenu) return;
    if (editingOrderHydratedRef.current === editingOrder.id) return;

    if (
      (!editingOrder.canBeModified && !editingOrder.canAddItems) ||
      !isWithinEditWindow(editingOrder.createdAt)
    ) {
      toast.error("Cette commande ne peut plus être modifiée");
      navigate("/pos", { replace: true });
      return;
    }

    const cart = cartFromOrder(editingOrder, {
      menu: activeMenu,
      combos,
      formulas,
    });
    const draftId = createDraftOrderId();
    const meta = (editingOrder.metadata ?? {}) as Record<string, unknown>;
    setDraftOrders([
      {
        id: draftId,
        orderType: editingOrder.orderType ?? "dine_in",
        tableId: editingOrder.restaurantTable?.id,
        notes: notesFromOrder(editingOrder),
        deliveryAddress: editingOrder.deliveryAddress ?? undefined,
        deliveryFee:
          typeof editingOrder.deliveryFee === "number"
            ? editingOrder.deliveryFee
            : undefined,
        deliveryPersonName:
          typeof meta.deliveryPersonName === "string"
            ? meta.deliveryPersonName
            : undefined,
        deliveryPersonPhone:
          typeof meta.deliveryPersonPhone === "string"
            ? meta.deliveryPersonPhone
            : undefined,
        guestCount:
          typeof meta.guestCount === "number" ? meta.guestCount : undefined,
        cart,
        createdAt: Date.now(),
        editingOrderId: editingOrder.id,
      },
    ]);
    setActiveOrderId(draftId);
    setIsCartOpen(true);
    editingOrderHydratedRef.current = editingOrder.id;
  }, [isEditMode, editingOrder, activeMenu, combos, formulas, navigate]);

  // Arriving with ?new=1 (e.g. the "Nouvelle commande" tile on the hub) jumps
  // straight into order creation instead of landing on an empty grid. Strip
  // the param afterwards so a refresh doesn't reopen the modal.
  useEffect(() => {
    if (searchParams.get("new") === null) return;
    setIsNewOrderModalOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete("new");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const totalMenuItems =
    activeMenu?.menuSections?.reduce(
      (sum, s) => sum + (s.menuItems?.length ?? 0),
      0
    ) ?? 0;

  const totalItems =
    totalMenuItems + combos.length + dailyDishes.length + formulas.length;

  const isSearching = searchQuery.trim().length > 0;

  const displayedItems = useMemo(() => {
    if (!activeMenu?.menuSections) return [];

    const query = searchQuery.toLowerCase();

    const allMenuItems = activeMenu.menuSections.flatMap((section) =>
      (section.menuItems ?? []).map((item) => ({
        ...item,
        sectionTitle: section.title,
      }))
    );

    const sortAvailableFirst = <T extends { isAvailable: boolean }>(
      items: T[]
    ) =>
      [...items].sort((a, b) => Number(b.isAvailable) - Number(a.isAvailable));

    if (isSearching) {
      return sortAvailableFirst(
        allMenuItems.filter((item) => item.title.toLowerCase().includes(query))
      );
    }

    if (
      selectedSectionId === null ||
      selectedSectionId === SECTION_COMBOS ||
      selectedSectionId === SECTION_DAILY_DISHES ||
      selectedSectionId === SECTION_FORMULAS
    ) {
      // These sections don't show menu items
      if (
        selectedSectionId === SECTION_COMBOS ||
        selectedSectionId === SECTION_DAILY_DISHES ||
        selectedSectionId === SECTION_FORMULAS
      ) {
        return [];
      }
      return sortAvailableFirst(allMenuItems);
    }

    const section = activeMenu.menuSections.find(
      (s) => s.id === selectedSectionId
    );
    return sortAvailableFirst(
      (section?.menuItems ?? []).map((item) => ({
        ...item,
        sectionTitle: undefined as string | undefined,
      }))
    );
  }, [activeMenu, selectedSectionId, searchQuery, isSearching]);

  // Combos and daily dishes to display
  const displayedCombos = useMemo(() => {
    const sortAvailable = (items: MenuCombo[]) =>
      [...items].sort((a, b) => Number(b.isAvailable) - Number(a.isAvailable));

    if (isSearching) {
      const query = searchQuery.toLowerCase();
      return sortAvailable(
        combos.filter((c) => c.name.toLowerCase().includes(query))
      );
    }
    if (selectedSectionId === null || selectedSectionId === SECTION_COMBOS) {
      return sortAvailable(combos);
    }
    return [];
  }, [combos, selectedSectionId, searchQuery, isSearching]);

  const displayedDailyDishes = useMemo(() => {
    if (isSearching) {
      const query = searchQuery.toLowerCase();
      return dailyDishes.filter((d) => d.name.toLowerCase().includes(query));
    }
    if (
      selectedSectionId === null ||
      selectedSectionId === SECTION_DAILY_DISHES
    ) {
      return dailyDishes;
    }
    return [];
  }, [dailyDishes, selectedSectionId, searchQuery, isSearching]);

  const displayedFormulas = useMemo(() => {
    if (isSearching) {
      const query = searchQuery.toLowerCase();
      return formulas.filter((f) => f.name.toLowerCase().includes(query));
    }
    if (selectedSectionId === null || selectedSectionId === SECTION_FORMULAS) {
      return formulas;
    }
    return [];
  }, [formulas, selectedSectionId, searchQuery, isSearching]);

  const hasDisplayedItems =
    displayedItems.length > 0 ||
    displayedCombos.length > 0 ||
    displayedDailyDishes.length > 0 ||
    displayedFormulas.length > 0;

  // ---------- Draft order management ----------

  const handleCreateOrder = (config: NewOrderConfig) => {
    const newOrder: DraftOrder = {
      id: createDraftOrderId(),
      orderType: config.orderType,
      tableId: config.tableId,
      cart: [],
      createdAt: Date.now(),
    };
    setDraftOrders((prev) => [...prev, newOrder]);
    setActiveOrderId(newOrder.id);
  };

  const exitEditMode = useCallback(() => {
    setDraftOrders([]);
    setActiveOrderId(null);
    setIsCartOpen(false);
    editingOrderHydratedRef.current = null;
    const next = new URLSearchParams(searchParams);
    next.delete("editOrder");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleDeleteOrder = useCallback(
    async (orderId: string) => {
      const order = draftOrders.find((o) => o.id === orderId);

      // In edit mode, "delete" means "abandon edits and exit edit mode".
      if (order?.editingOrderId) {
        const ok = await confirm({
          title: "Annuler les modifications ?",
          description: "Vos changements seront perdus.",
          confirmText: "Quitter sans enregistrer",
          cancelText: "Continuer la modification",
          variant: "destructive",
          icon: <Trash2 className="h-6 w-6" />,
        });
        if (!ok) return;
        exitEditMode();
        return;
      }

      const hasItems =
        (order?.cart.reduce((s, c) => s + c.quantity, 0) ?? 0) > 0;

      if (hasItems) {
        const ok = await confirm({
          title: "Supprimer cette commande ?",
          description: "Tous les articles ajoutés seront perdus.",
          confirmText: "Supprimer",
          cancelText: "Annuler",
          variant: "destructive",
          icon: <Trash2 className="h-6 w-6" />,
        });
        if (!ok) return;
      }

      setDraftOrders((prev) => prev.filter((o) => o.id !== orderId));
      setActiveOrderId((currentId) => {
        if (currentId !== orderId) return currentId;
        const remaining = draftOrders.filter((o) => o.id !== orderId);
        return remaining.length > 0 ? remaining[0].id : null;
      });
    },
    [draftOrders, confirm, exitEditMode]
  );

  const updateActiveOrderCart = useCallback(
    (updater: (cart: CartEntry[]) => CartEntry[]) => {
      setDraftOrders((prev) =>
        prev.map((o) =>
          o.id === activeOrderId ? { ...o, cart: updater(o.cart) } : o
        )
      );
    },
    [activeOrderId]
  );

  const updateOrderConfig = useCallback(
    (
      orderId: string,
      updates: Partial<
        Pick<
          DraftOrder,
          | "tableId"
          | "notes"
          | "modificationReason"
          | "deliveryAddress"
          | "deliveryFee"
          | "deliveryPersonName"
          | "deliveryPersonPhone"
          | "guestCount"
        >
      >
    ) => {
      setDraftOrders((prev) =>
        prev.map((o) => (o.id === orderId ? { ...o, ...updates } : o))
      );
    },
    []
  );

  // ---------- Cart operations ----------

  const getCartQuantity = useCallback(
    (itemId: string) =>
      cart
        .filter(
          (c) =>
            c.menuItem?.id === itemId ||
            c.menuComboId === itemId ||
            c.dailyDishId === itemId ||
            c.menuFormulaId === itemId
        )
        .reduce((sum, c) => sum + c.quantity, 0),
    [cart]
  );

  const handleItemClick = useCallback(
    (item: MenuItem) => {
      if (!item.isAvailable) return;
      if (!activeOrder) {
        setIsNewOrderModalOpen(true);
        return;
      }

      setOptionSheetItem(item);
      setIsOptionSheetOpen(true);
    },
    [activeOrder]
  );

  const handleSimpleItemClick = useCallback(
    (
      id: string,
      type: "combo" | "daily_dish",
      name: string,
      price: number,
      imageUrl?: string
    ) => {
      if (!activeOrder) {
        setIsNewOrderModalOpen(true);
        return;
      }
      setSimpleItemModal({ id, type, name, price, imageUrl });
    },
    [activeOrder]
  );

  const handleComboClick = useCallback(
    (combo: MenuCombo) => {
      if (!combo.isAvailable) return;
      if (!activeOrder) {
        setIsNewOrderModalOpen(true);
        return;
      }
      setComboConfigItem(combo);
    },
    [activeOrder]
  );

  const handleFormulaClick = useCallback(
    (formula: MenuFormula) => {
      if (!activeOrder) {
        setIsNewOrderModalOpen(true);
        return;
      }
      setFormulaConfigItem(formula);
    },
    [activeOrder]
  );

  const addFormulaToCart = useCallback(
    (
      formula: MenuFormula,
      quantity: number,
      additionalDemands: string,
      formulaSelections: FormulaSelection[]
    ) => {
      if (!activeOrderId) return;

      const selectionKey = formulaSelections
        .map(
          (s) =>
            `${s.componentItemId}:${(s.selectedOptionValueIds ?? []).sort().join("+")}`
        )
        .sort()
        .join(",");
      const entryId = buildCartEntryId(
        formula.id,
        "formula",
        [],
        `${selectionKey}::${additionalDemands}`
      );

      // "Végétarienne (Moyenne)": the chosen options are part of what the
      // kitchen and the customer see, so they belong on the cart line too.
      const selectionLabels = formulaSelections.map((s) => {
        const name = s.itemName;
        const options = s.selectedOptionLabels ?? [];
        return options.length > 0 ? `${name} (${options.join(", ")})` : name;
      });

      // Calculate unit price: base + supplements + option surcharges
      const supplement = formulaSelections.reduce(
        (sum, s) => sum + s.priceModifier + (s.optionsSurcharge ?? 0),
        0
      );
      const unitPrice = formula.price + supplement;

      const cartSelections: FormulaCartSelection[] = formulaSelections.map(
        (s) => ({
          menuFormulaComponentItemId: s.componentItemId,
          menuItemId: s.menuItemId,
          selectedOptions: s.selectedOptionValueIds,
        })
      );

      updateActiveOrderCart((prev) => {
        const existing = prev.find((c) => c.id === entryId);
        if (existing) {
          return prev.map((c) =>
            c.id === entryId ? { ...c, quantity: c.quantity + quantity } : c
          );
        }
        return [
          ...prev,
          {
            id: entryId,
            type: "formula" as const,
            menuFormulaId: formula.id,
            name: formula.name,
            imageUrl: formula.image?.url,
            quantity,
            selectedOptionValueIds: [],
            selectedOptionLabels: selectionLabels,
            additionalDemands,
            unitPrice,
            formulaSelections: cartSelections,
          },
        ];
      });
    },
    [activeOrderId, updateActiveOrderCart]
  );

  const addComboToCart = useCallback(
    (
      combo: MenuCombo,
      quantity: number,
      additionalDemands: string,
      comboSelections: ComboSelection[]
    ) => {
      if (!activeOrderId) return;

      const cartSelections = comboSelections.map((s) => ({
        menuItem: s.menuItemId,
        sideType: s.sideType,
        ...(s.selectedOptionValueIds && s.selectedOptionValueIds.length > 0
          ? { selectedOptions: s.selectedOptionValueIds }
          : {}),
      }));
      const entryId = buildCartEntryId(
        combo.id,
        "combo",
        [],
        `${comboSelectionKey(cartSelections)}::${additionalDemands}`
      );

      // "Cheese burger (Double)": the dish's options belong on the cart line.
      // The API prices a combo at its own price only (MenuComboPriceCalculator),
      // so option surcharges are not added to unitPrice.
      const selectionLabels = comboSelections.map((s) =>
        s.sideType
          ? formatSideType(s.sideType)
          : comboSelectionLabel(s.menuItemTitle, s.selectedOptionLabels ?? [])
      );

      updateActiveOrderCart((prev) => {
        const existing = prev.find((c) => c.id === entryId);
        if (existing) {
          return prev.map((c) =>
            c.id === entryId ? { ...c, quantity: c.quantity + quantity } : c
          );
        }
        return [
          ...prev,
          {
            id: entryId,
            type: "combo" as const,
            menuComboId: combo.id,
            name: combo.name,
            imageUrl: combo.image?.url,
            quantity,
            selectedOptionValueIds: [],
            selectedOptionLabels: selectionLabels,
            additionalDemands,
            unitPrice: combo.price,
            comboSelections: cartSelections,
          },
        ];
      });
    },
    [activeOrderId, updateActiveOrderCart]
  );

  const addSimpleToCart = useCallback(
    (
      id: string,
      type: "combo" | "daily_dish",
      name: string,
      price: number,
      imageUrl: string | undefined,
      quantity: number,
      additionalDemands: string
    ) => {
      if (!activeOrderId) return;

      const entryId = buildCartEntryId(id, type, [], additionalDemands);

      updateActiveOrderCart((prev) => {
        const existing = prev.find((c) => c.id === entryId);
        if (existing) {
          return prev.map((c) =>
            c.id === entryId ? { ...c, quantity: c.quantity + quantity } : c
          );
        }
        return [
          ...prev,
          {
            id: entryId,
            type,
            menuComboId: type === "combo" ? id : undefined,
            dailyDishId: type === "daily_dish" ? id : undefined,
            name,
            imageUrl,
            quantity,
            selectedOptionValueIds: [],
            selectedOptionLabels: [],
            additionalDemands,
            unitPrice: price,
          },
        ];
      });
    },
    [activeOrderId, updateActiveOrderCart]
  );

  const addToCart = useCallback(
    (
      item: MenuItem,
      quantity: number,
      selectedOptionValueIds: string[],
      additionalDemands: string
    ) => {
      if (!activeOrderId) return;

      const entryId = buildCartEntryId(
        item.id,
        "item",
        selectedOptionValueIds,
        additionalDemands
      );

      let optionsPrice = 0;
      const selectedLabels: string[] = [];

      for (const itemOption of item.itemOptions ?? []) {
        for (const valueId of selectedOptionValueIds) {
          const value = itemOption.menuItemOption.menuItemOptionValues.find(
            (v: MenuItemOptionValue) => v.id === valueId
          );
          if (!value) continue;

          selectedLabels.push(value.title);

          // 1. Direct price modifier
          const optionPrice = itemOption.itemOptionPrices.find(
            (p) => p.menuItemOptionValue.id === valueId
          );
          if (optionPrice) {
            optionsPrice += optionPrice.priceModifier ?? 0;
            continue;
          }

          // 2. Template option: use value's basePrice
          if (itemOption.menuItemOption.isTemplate) {
            optionsPrice += value.basePrice ?? 0;
            continue;
          }

          // 3. Dependent option: lookup price based on parent's selected value
          if (itemOption.menuItemOption.dependsOnOption) {
            const parentOptionId = itemOption.menuItemOption.dependsOnOption.id;
            const parentItemOption = item.itemOptions.find(
              (opt) => opt.menuItemOption.id === parentOptionId
            );
            if (parentItemOption) {
              const parentValueId = selectedOptionValueIds.find((id) =>
                parentItemOption.menuItemOption.menuItemOptionValues.some(
                  (v: MenuItemOptionValue) => v.id === id
                )
              );
              if (parentValueId) {
                const depPrice = value.valueDependentPrices?.find(
                  (dp) => dp.dependsOnValue?.id === parentValueId
                );
                if (depPrice) {
                  optionsPrice += depPrice.priceModifier ?? 0;
                }
              }
            }
          }
        }
      }

      const unitPrice = item.basePrice + optionsPrice;

      updateActiveOrderCart((prev) => {
        const existing = prev.find((c) => c.id === entryId);
        if (existing) {
          return prev.map((c) =>
            c.id === entryId ? { ...c, quantity: c.quantity + quantity } : c
          );
        }
        return [
          ...prev,
          {
            id: entryId,
            type: "item" as const,
            menuItem: item,
            name: item.title,
            imageUrl: item.image?.url,
            quantity,
            selectedOptionValueIds,
            selectedOptionLabels: selectedLabels,
            additionalDemands,
            unitPrice,
          },
        ];
      });
    },
    [activeOrderId, updateActiveOrderCart]
  );

  const updateCartEntry = useCallback(
    (entryId: string, delta: number) => {
      updateActiveOrderCart((prev) => {
        const entry = prev.find((c) => c.id === entryId);
        if (!entry) return prev;
        const newQty = entry.quantity + delta;
        // Existing order lines are removed via the trash icon, not by counting
        // to zero: add-only locks them at the committed quantity, full edit
        // stops at 1. Freshly-added lines still remove when minus hits zero.
        const minQty = entry.existingIri
          ? editAddOnly
            ? entry.originalQuantity ?? entry.quantity
            : 1
          : 0;
        if (newQty < minQty) return prev;
        if (newQty <= 0) {
          return prev.filter((c) => c.id !== entryId);
        }
        return prev.map((c) =>
          c.id === entryId ? { ...c, quantity: newQty } : c
        );
      });
    },
    [updateActiveOrderCart, editAddOnly]
  );

  const updateSimpleItemQuantity = useCallback(
    (item: MenuItem, delta: number) => {
      if (!activeOrder) {
        setIsNewOrderModalOpen(true);
        return;
      }

      const currentQty = getCartQuantity(item.id);

      // First add (0 -> 1): open modal
      if (delta > 0 && currentQty === 0) {
        setOptionSheetItem(item);
        setIsOptionSheetOpen(true);
        return;
      }

      if (item.itemOptions && item.itemOptions.length > 0) {
        updateActiveOrderCart((prev) => {
          const entries = prev.filter((c) => c.menuItem?.id === item.id);
          if (entries.length === 0) return prev;
          const lastEntry = entries[entries.length - 1];
          const newQty = lastEntry.quantity + delta;
          if (newQty <= 0) {
            return prev.filter((c) => c.id !== lastEntry.id);
          }
          return prev.map((c) =>
            c.id === lastEntry.id ? { ...c, quantity: newQty } : c
          );
        });
        return;
      }
      const entryId = buildCartEntryId(item.id, "item", [], "");
      if (delta > 0) {
        addToCart(item, delta, [], "");
      } else {
        updateCartEntry(entryId, delta);
      }
    },
    [
      activeOrder,
      getCartQuantity,
      addToCart,
      updateCartEntry,
      updateActiveOrderCart,
    ]
  );

  const removeFromCart = useCallback(
    (entryId: string) => {
      updateActiveOrderCart((prev) => {
        const entry = prev.find((c) => c.id === entryId);
        // Add-only: committed lines can't be removed (would be a 422).
        if (editAddOnly && entry?.existingIri) return prev;
        return prev.filter((c) => c.id !== entryId);
      });
    },
    [updateActiveOrderCart, editAddOnly]
  );

  const cartTotal = cart.reduce((sum, c) => sum + c.unitPrice * c.quantity, 0);
  const cartItemsCount = cart.reduce((sum, c) => sum + c.quantity, 0);

  // ---------- Submit order ----------

  const handleSubmitOrder = async () => {
    if (!activeOrder || activeOrder.cart.length === 0) {
      toast.error("Le panier est vide");
      return;
    }
    if (activeOrder.orderType === "dine_in" && !activeOrder.tableId) {
      toast.error("Veuillez sélectionner une table");
      return;
    }
    if (!restaurant || !activeMenu) return;

    // Edit mode: submit immediately. Customer/delivery details aren't part
    // of this flow and are left untouched on the server.
    if (isEditMode) {
      try {
        await submitEdit();
      } catch (err) {
        toast.error(translateOrderUpdateError(err));
      }
      return;
    }

    // Create mode: collect customer/delivery details before submitting.
    setIsConfirmModalOpen(true);
  };

  const submitEdit = async () => {
    if (!activeOrder?.editingOrderId || !editingOrder) return;

    const payload = buildUpdatePayload({
      draft: activeOrder,
      originalOrder: editingOrder,
    });

    setIsSubmitting(true);
    try {
      await updateOrder({
        id: activeOrder.editingOrderId,
        data: payload,
      }).unwrap();
      toast.success("Commande mise à jour avec succès");
      // Clear the edit draft so re-entering POS doesn't show stale state.
      const updatedId = activeOrder.editingOrderId;
      setDraftOrders([]);
      setActiveOrderId(null);
      setIsCartOpen(false);
      // Replace URL: leave edit mode and surface the updated order in the
      // details drawer at the page level.
      setSearchParams({ showOrder: updatedId }, { replace: true });
    } catch (error) {
      toast.error(translateOrderUpdateError(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitOrder = async (details: ConfirmOrderDetails) => {
    if (!activeOrder || !restaurant || !activeMenu) return;

    const itemsPayload = buildOrderItemsPayload(activeOrder.cart);
    const createMetadata = buildCreateMetadata(details);

    const request: CreatePosOrderRequest = {
      menu: `/api/menus/${activeMenu.id}`,
      ...(activeOrder.tableId
        ? { restaurantTable: `/api/restaurant_tables/${activeOrder.tableId}` }
        : {}),
      source: "pos",
      orderType: activeOrder.orderType,
      ...(details.deliveryAddress
        ? { deliveryAddress: details.deliveryAddress }
        : {}),
      ...(details.deliveryFee ? { deliveryFee: details.deliveryFee } : {}),
      ...(details.customerPhone
        ? { customerPhone: details.customerPhone }
        : {}),
      ...(details.customerFullName
        ? { customerFullName: details.customerFullName }
        : {}),
      ...(activeOrder.notes?.trim()
        ? { notes: activeOrder.notes.trim() }
        : {}),
      ...(createMetadata ? { metadata: createMetadata } : {}),
      orderItems: itemsPayload.orderItems,
      ...(itemsPayload.menuComboOrderItems
        ? { menuComboOrderItems: itemsPayload.menuComboOrderItems }
        : {}),
      ...(itemsPayload.menuFormulaOrderItems
        ? { menuFormulaOrderItems: itemsPayload.menuFormulaOrderItems }
        : {}),
    };

    setIsSubmitting(true);

    // On error (including no network: there is no offline queue) the
    // rejection propagates so the modal stays open with the draft and shows
    // the error inline.
    try {
      const response = await createPosOrder(request).unwrap();
      toast.success("Commande créée avec succès");
      if (response?.stockWarnings && response.stockWarnings.length > 0) {
        setStockWarnings(response.stockWarnings);
      }
      const remaining = draftOrders.filter((o) => o.id !== activeOrder.id);
      setDraftOrders(remaining);
      setActiveOrderId(remaining.length > 0 ? remaining[0].id : null);
      setIsCartOpen(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ---------- Render ----------

  if (!restaurant) {
    return (
      <DashLayout>
        <div className="flex items-center justify-center py-12 text-muted-foreground">
          Chargement des données du restaurant...
        </div>
      </DashLayout>
    );
  }

  if (isLoading) {
    return (
      <DashLayout>
        <div className="space-y-6">
          <div className="h-10 w-72 bg-muted rounded-lg animate-pulse" />
          <div className="flex items-center gap-2">
            {[...Array(4)].map((_, i) => (
              <div
                key={i}
                className="flex items-center gap-2 py-2 px-4 rounded-full border border-border animate-pulse"
              >
                <div className="w-6 h-6 bg-muted rounded-full" />
                <div className="w-16 h-4 bg-muted rounded" />
              </div>
            ))}
          </div>
          <div className="h-12 w-full max-w-md bg-muted rounded-lg animate-pulse" />
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {[...Array(8)].map((_, i) => (
              <div
                key={i}
                className="rounded-xl border border-border bg-card p-3 flex flex-col items-center gap-2 animate-pulse"
              >
                <div className="w-24 h-24 bg-muted rounded-full" />
                <div className="w-full space-y-1.5">
                  <div className="h-3 w-12 bg-muted rounded" />
                  <div className="h-4 w-24 bg-muted rounded" />
                </div>
                <div className="w-full flex items-center justify-between">
                  <div className="h-4 w-16 bg-muted rounded" />
                  <div className="h-6 w-6 bg-muted rounded-full" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </DashLayout>
    );
  }

  if (!activeMenu) {
    return (
      <DashLayout>
        <div className="flex flex-col items-center justify-center py-12 text-muted-foreground gap-2">
          <UtensilsCrossed size={40} strokeWidth={1.5} />
          <p>
            Aucun menu actif. Veuillez activer un menu pour utiliser la caisse.
          </p>
        </div>
      </DashLayout>
    );
  }

  if (!isEditMode && !isLoadingSessions && !hasOpenSession) {
    return (
      <DashLayout>
        <div className="flex flex-col items-center justify-center py-24 text-muted-foreground gap-4">
          <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center">
            <Banknote size={32} className="text-red-500" />
          </div>
          <div className="text-center space-y-1">
            <p className="text-lg font-medium text-card-foreground">
              Aucune session de caisse ouverte
            </p>
            <p className="text-sm">
              Ouvrez une session de caisse pour commencer à prendre des
              commandes.
            </p>
          </div>
          {!isWaiter && (
            <Button asChild>
              <Link to="/register">Ouvrir une session</Link>
            </Button>
          )}
        </div>
      </DashLayout>
    );
  }

  if (isEditMode && isLoadingEditOrder && !editingOrder) {
    return (
      <DashLayout>
        <div className="flex items-center justify-center py-24 text-muted-foreground">
          Chargement de la commande à modifier...
        </div>
      </DashLayout>
    );
  }

  return (
    <DashLayout>
      <div className="space-y-4 pb-20">
        {stockWarnings.length > 0 && (
          <StockWarningsBanner
            warnings={stockWarnings}
            onDismiss={() => setStockWarnings([])}
          />
        )}

        {/* Draft orders bar — hidden in edit mode (single in-flight order) */}
        {!isEditMode && (
        <div className="flex items-center gap-2 overflow-x-auto scroll-hidden">
          <Button
            size="sm"
            onClick={() => setIsNewOrderModalOpen(true)}
            className="flex items-center gap-1.5 whitespace-nowrap"
          >
            <Plus size={16} />
            Nouvelle commande
          </Button>

          {draftOrders.map((order, index) => {
            const orderItemsCount = order.cart.reduce(
              (s, c) => s + c.quantity,
              0
            );
            const isActive = order.id === activeOrderId;
            let label: string;
            if (order.orderType !== "dine_in") {
              label = ORDER_TYPE_LABELS[order.orderType];
            } else {
              const tableCode = order.tableId
                ? tables.find((t) => t.id === order.tableId)?.code
                : undefined;
              label = tableCode
                ? `Table ${tableCode}`
                : `Commande ${index + 1}`;
            }

            return (
              <div
                key={order.id}
                className={cn(
                  "flex items-center rounded-lg border text-sm whitespace-nowrap transition-all",
                  isActive
                    ? "border-primary bg-primary/5 text-primary font-medium"
                    : "border-border bg-card text-muted-foreground hover:border-primary/30"
                )}
              >
                <button
                  onClick={() => setActiveOrderId(order.id)}
                  className="flex items-center gap-2 py-1.5 pl-3 pr-2"
                >
                  <span>
                    Cmd {index + 1} — {label}
                  </span>
                  {orderItemsCount > 0 && (
                    <span className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded-full font-semibold">
                      {orderItemsCount}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => handleDeleteOrder(order.id)}
                  aria-label="Supprimer la commande"
                  className="p-1 mr-1 rounded hover:bg-muted/80 transition-colors"
                >
                  <X size={14} />
                </button>
              </div>
            );
          })}

          {draftOrders.length === 0 && (
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              Commencez par créer une commande
            </span>
          )}
        </div>
        )}

        {/* Active orders for quick access without leaving the POS — hidden in edit mode */}
        {!isEditMode && <PosActiveOrders />}

        {/* Edit-mode header — context for the page */}
        {isEditMode && editingOrder && (
          <div className="flex items-center justify-between gap-3 rounded-lg border border-primary/30 bg-primary/5 px-4 py-3">
            <div className="text-sm">
              <p className="font-semibold text-primary">
                Modification en cours — #
                {editingOrder.dailySequence ?? editingOrder.orderNumber}
              </p>
              <p className="text-xs text-muted-foreground">
                Les changements seront enregistrés sur la commande existante.
              </p>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={exitEditMode}
            >
              Retour
            </Button>
          </div>
        )}

        {/* Section tabs */}
        <div className="flex items-center gap-2 overflow-x-auto pb-2 scroll-hidden">
          <button
            onClick={() => setSelectedSectionId(null)}
            className={cn(
              "flex items-center gap-2 py-2 px-4 rounded-full border transition-all text-sm whitespace-nowrap",
              selectedSectionId === null && !isSearching
                ? "border-primary bg-primary/5 text-primary font-medium"
                : "border-border bg-card text-muted-foreground hover:border-primary/30"
            )}
          >
            <LayoutGrid className="w-5 h-5 flex-shrink-0" />
            <span>Toutes ({totalItems})</span>
          </button>

          {dailyDishes.length > 0 && (
            <button
              onClick={() => setSelectedSectionId(SECTION_DAILY_DISHES)}
              className={cn(
                "flex items-center gap-2 py-2 px-4 rounded-full border transition-all text-sm whitespace-nowrap",
                selectedSectionId === SECTION_DAILY_DISHES && !isSearching
                  ? "border-primary bg-primary/5 text-primary font-medium"
                  : "border-border bg-card text-muted-foreground hover:border-primary/30"
              )}
            >
              <ChefHat className="w-5 h-5 flex-shrink-0" />
              <span>Plats du jour ({dailyDishes.length})</span>
            </button>
          )}

          {combos.length > 0 && (
            <button
              onClick={() => setSelectedSectionId(SECTION_COMBOS)}
              className={cn(
                "flex items-center gap-2 py-2 px-4 rounded-full border transition-all text-sm whitespace-nowrap",
                selectedSectionId === SECTION_COMBOS && !isSearching
                  ? "border-primary bg-primary/5 text-primary font-medium"
                  : "border-border bg-card text-muted-foreground hover:border-primary/30"
              )}
            >
              <img
                src="/images/menus.png"
                alt="Combos"
                className="w-6 h-6 object-cover rounded-full flex-shrink-0"
              />
              <span>Combos ({combos.length})</span>
            </button>
          )}

          {formulas.length > 0 && (
            <button
              onClick={() => setSelectedSectionId(SECTION_FORMULAS)}
              className={cn(
                "flex items-center gap-2 py-2 px-4 rounded-full border transition-all text-sm whitespace-nowrap",
                selectedSectionId === SECTION_FORMULAS && !isSearching
                  ? "border-primary bg-primary/5 text-primary font-medium"
                  : "border-border bg-card text-muted-foreground hover:border-primary/30"
              )}
            >
              <ClipboardList className="w-5 h-5 flex-shrink-0" />
              <span>Formules ({formulas.length})</span>
            </button>
          )}

          {activeMenu.menuSections?.map((section) => {
            const isSelected = selectedSectionId === section.id && !isSearching;
            const itemCount = section.menuItems?.length ?? 0;

            return (
              <button
                key={section.id}
                onClick={() => setSelectedSectionId(section.id)}
                className={cn(
                  "flex items-center gap-2 py-2 px-4 rounded-full border transition-all text-sm whitespace-nowrap",
                  isSelected
                    ? "border-primary bg-primary/5 text-primary font-medium"
                    : "border-border bg-card text-muted-foreground hover:border-primary/30"
                )}
              >
                <img
                  src={getImageUrl(section?.image?.url)}
                  alt={section?.title}
                  className="w-6 h-6 object-cover rounded-full flex-shrink-0"
                />
                <span>
                  {section.title} ({itemCount})
                </span>
              </button>
            );
          })}
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search
            size={18}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <input
            type="search"
            placeholder="Rechercher un article..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-3 py-3 rounded-lg border border-border bg-card text-sm placeholder:text-muted-foreground outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors"
          />
        </div>

        {/* Empty cart hint */}
        {activeOrder && cart.length === 0 && (
          <div className="flex items-center gap-2.5 text-sm text-muted-foreground">
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Info size={15} />
            </span>
            <span>
              Sélectionnez des articles ci-dessous pour les ajouter à la
              commande.
            </span>
          </div>
        )}

        {/* Items grid */}
        {hasDisplayedItems ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4">
            {displayedDailyDishes.map((dish) => (
              <DailyDishCard
                key={`dish-${dish.id}`}
                dish={dish}
                currency={currency}
                quantity={getCartQuantity(dish.id!)}
                hasActiveOrder={!!activeOrder}
                showSection={selectedSectionId === null || isSearching}
                onCardClick={() =>
                  handleSimpleItemClick(
                    dish.id!,
                    "daily_dish",
                    dish.name,
                    dish.price,
                    dish.image?.url
                  )
                }
                onUpdateQuantity={(delta) => {
                  const currentQty = getCartQuantity(dish.id!);
                  if (delta > 0 && currentQty === 0) {
                    handleSimpleItemClick(
                      dish.id!,
                      "daily_dish",
                      dish.name,
                      dish.price,
                      dish.image?.url
                    );
                  } else if (delta > 0) {
                    addSimpleToCart(
                      dish.id!,
                      "daily_dish",
                      dish.name,
                      dish.price,
                      dish.image?.url,
                      1,
                      ""
                    );
                  } else {
                    updateCartEntry(
                      buildCartEntryId(dish.id!, "daily_dish", [], ""),
                      delta
                    );
                  }
                }}
              />
            ))}

            {displayedCombos.map((combo) => (
              <ComboCard
                key={`combo-${combo.id}`}
                combo={combo}
                currency={currency}
                quantity={getCartQuantity(combo.id)}
                hasActiveOrder={!!activeOrder}
                showSection={selectedSectionId === null || isSearching}
                onCardClick={() => handleComboClick(combo)}
                onUpdateQuantity={(delta) => {
                  const currentQty = getCartQuantity(combo.id);
                  if (delta > 0 && currentQty === 0) {
                    handleComboClick(combo);
                  } else if (delta > 0) {
                    // Increment last entry
                    updateActiveOrderCart((prev) => {
                      const entries = prev.filter(
                        (c) => c.menuComboId === combo.id
                      );
                      if (entries.length === 0) return prev;
                      const lastEntry = entries[entries.length - 1];
                      return prev.map((c) =>
                        c.id === lastEntry.id
                          ? { ...c, quantity: c.quantity + 1 }
                          : c
                      );
                    });
                  } else {
                    // Decrement last entry
                    updateActiveOrderCart((prev) => {
                      const entries = prev.filter(
                        (c) => c.menuComboId === combo.id
                      );
                      if (entries.length === 0) return prev;
                      const lastEntry = entries[entries.length - 1];
                      const newQty = lastEntry.quantity + delta;
                      if (newQty <= 0)
                        return prev.filter((c) => c.id !== lastEntry.id);
                      return prev.map((c) =>
                        c.id === lastEntry.id ? { ...c, quantity: newQty } : c
                      );
                    });
                  }
                }}
              />
            ))}

            {displayedFormulas.map((formula) => (
              <FormulaCard
                key={`formula-${formula.id}`}
                formula={formula}
                currency={currency}
                quantity={getCartQuantity(formula.id)}
                hasActiveOrder={!!activeOrder}
                showSection={selectedSectionId === null || isSearching}
                onCardClick={() => handleFormulaClick(formula)}
                onUpdateQuantity={(delta) => {
                  const currentQty = getCartQuantity(formula.id);
                  if (delta > 0 && currentQty === 0) {
                    handleFormulaClick(formula);
                  } else if (delta > 0) {
                    updateActiveOrderCart((prev) => {
                      const entries = prev.filter(
                        (c) => c.menuFormulaId === formula.id
                      );
                      if (entries.length === 0) return prev;
                      const lastEntry = entries[entries.length - 1];
                      return prev.map((c) =>
                        c.id === lastEntry.id
                          ? { ...c, quantity: c.quantity + 1 }
                          : c
                      );
                    });
                  } else {
                    updateActiveOrderCart((prev) => {
                      const entries = prev.filter(
                        (c) => c.menuFormulaId === formula.id
                      );
                      if (entries.length === 0) return prev;
                      const lastEntry = entries[entries.length - 1];
                      const newQty = lastEntry.quantity + delta;
                      if (newQty <= 0)
                        return prev.filter((c) => c.id !== lastEntry.id);
                      return prev.map((c) =>
                        c.id === lastEntry.id ? { ...c, quantity: newQty } : c
                      );
                    });
                  }
                }}
              />
            ))}

            {displayedItems.map((item) => (
              <PosItemCard
                key={item.id}
                item={item}
                sectionTitle={item.sectionTitle}
                currency={currency}
                quantity={getCartQuantity(item.id)}
                hasActiveOrder={!!activeOrder}
                onCardClick={() => handleItemClick(item)}
                onUpdateQuantity={(delta) =>
                  updateSimpleItemQuantity(item, delta)
                }
              />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground gap-2">
            <Search size={32} strokeWidth={1.5} />
            <p className="text-sm">
              {isSearching
                ? `Aucun résultat pour "${searchQuery}"`
                : "Aucun article dans cette section"}
            </p>
          </div>
        )}
      </div>

      {/* Floating action button — new order when empty, cart otherwise.
         In edit mode there's only ever the cart action. */}
      {!isCartOpen &&
        (draftOrders.length === 0 && !isEditMode ? (
          <button
            onClick={() => setIsNewOrderModalOpen(true)}
            aria-label="Nouvelle commande"
            className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-lg hover:bg-primary/90 transition-all flex items-center justify-center"
          >
            <Plus size={24} />
          </button>
        ) : draftOrders.length > 0 ? (
          <button
            onClick={() => setIsCartOpen(true)}
            aria-label="Ouvrir le panier"
            className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-lg hover:bg-primary/90 transition-all flex items-center justify-center"
          >
            <ShoppingCart size={22} />
            {cartItemsCount > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-destructive text-white text-xs font-bold flex items-center justify-center">
                {cartItemsCount}
              </span>
            )}
          </button>
        ) : null)}

      {/* Cart panel backdrop */}
      {isCartOpen && (
        <button
          onClick={() => setIsCartOpen(false)}
          className="fixed inset-0 bg-black/50 z-40"
        />
      )}

      {/* Cart panel */}
      <div
        className={cn(
          "fixed inset-y-0 right-0 w-full md:w-[400px] z-50 bg-card border-l border-border shadow-xl flex flex-col transform transition-transform duration-300 ease-in-out",
          isCartOpen ? "translate-x-0" : "translate-x-full"
        )}
      >
        <CartPanel
          order={activeOrder}
          cart={cart}
          currency={currency}
          cartTotal={cartTotal}
          cartItemsCount={cartItemsCount}
          tables={tables}
          onUpdateQuantity={updateCartEntry}
          onRemoveItem={removeFromCart}
          onClose={() => setIsCartOpen(false)}
          onSubmit={handleSubmitOrder}
          isSubmitting={isSubmitting}
          onUpdateOrderConfig={updateOrderConfig}
          onDeleteOrder={handleDeleteOrder}
          addOnly={editAddOnly}
          askGuestCount={askGuestCount}
          editOrderLabel={
            isEditMode && editingOrder
              ? String(editingOrder.dailySequence ?? editingOrder.orderNumber)
              : undefined
          }
          editWarning={
            editAddOnly
              ? "Cette commande est déjà prête ou servie. Vous pouvez uniquement ajouter de nouveaux articles ; les articles existants ne peuvent pas être retirés."
              : isEditMode && editingOrder?.status === "preparing"
                ? "Cette commande est déjà en préparation. Le retrait d'articles déjà préparés peut nécessiter un ajustement manuel du stock."
                : null
          }
        />
      </div>

      {/* Item option modal */}
      <ItemOptionSheet
        open={isOptionSheetOpen}
        onClose={() => {
          setIsOptionSheetOpen(false);
          setOptionSheetItem(null);
        }}
        item={optionSheetItem}
        currency={currency}
        onAddToCart={addToCart}
      />

      {/* Combo config modal */}
      <ComboConfigModal
        open={!!comboConfigItem}
        onClose={() => setComboConfigItem(null)}
        combo={comboConfigItem}
        currency={currency}
        menuItemsById={menuItemsById}
        onAdd={(quantity, additionalDemands, selections) => {
          if (!comboConfigItem) return;
          addComboToCart(
            comboConfigItem,
            quantity,
            additionalDemands,
            selections
          );
          setComboConfigItem(null);
        }}
      />

      {/* Formula config modal */}
      <FormulaConfigModal
        open={!!formulaConfigItem}
        onClose={() => setFormulaConfigItem(null)}
        formula={formulaConfigItem}
        currency={currency}
        onAdd={(quantity, additionalDemands, selections) => {
          if (!formulaConfigItem) return;
          addFormulaToCart(
            formulaConfigItem,
            quantity,
            additionalDemands,
            selections
          );
          setFormulaConfigItem(null);
        }}
      />

      {/* Simple item modal (daily dishes) */}
      <SimpleItemModal
        open={!!simpleItemModal}
        onClose={() => setSimpleItemModal(null)}
        name={simpleItemModal?.name ?? ""}
        price={simpleItemModal?.price ?? 0}
        imageUrl={simpleItemModal?.imageUrl}
        currency={currency}
        onAdd={(quantity, additionalDemands) => {
          if (!simpleItemModal) return;
          addSimpleToCart(
            simpleItemModal.id,
            simpleItemModal.type,
            simpleItemModal.name,
            simpleItemModal.price,
            simpleItemModal.imageUrl,
            quantity,
            additionalDemands
          );
          setSimpleItemModal(null);
        }}
      />

      {/* New order modal */}
      <NewOrderModal
        open={isNewOrderModalOpen}
        onClose={() => setIsNewOrderModalOpen(false)}
        onCreate={handleCreateOrder}
      />

      {/* Order details drawer — opens when ?showOrder=<id> is present
         (e.g. just after a successful staff edit). */}
      {showOrderId && (
        <OrderDetailsDrawer
          orderId={showOrderId}
          isOpen
          onClose={() => {
            const next = new URLSearchParams(searchParams);
            next.delete("showOrder");
            setSearchParams(next, { replace: true });
          }}
        />
      )}

      {/* Confirm order modal (customer/delivery details, create flow only) */}
      {activeOrder && !isEditMode && (
        <ConfirmOrderModal
          open={isConfirmModalOpen}
          onClose={() => setIsConfirmModalOpen(false)}
          orderType={activeOrder.orderType}
          askGuestCount={askGuestCount}
          onConfirm={submitOrder}
        />
      )}
    </DashLayout>
  );
};

// ---------- ComboCard ----------

function ComboCard({
  combo,
  currency,
  quantity,
  hasActiveOrder,
  showSection,
  onCardClick,
  onUpdateQuantity,
}: {
  combo: MenuCombo;
  currency: string;
  quantity: number;
  hasActiveOrder: boolean;
  showSection: boolean;
  onCardClick: () => void;
  onUpdateQuantity: (delta: number) => void;
}) {
  return (
    <div
      onClick={onCardClick}
      className={cn(
        "rounded-xl border bg-card p-3 flex flex-col items-center gap-3 transition-all cursor-pointer group relative",
        !combo.isAvailable && "pointer-events-none",
        quantity > 0
          ? "border-primary shadow-sm"
          : "border-border hover:border-primary/30 hover:shadow-sm"
      )}
    >
      <div className="w-full bg-gray-50 rounded-lg flex items-center justify-center py-2 lg:py-4">
        <div className="w-24 h-24 rounded-full overflow-hidden relative">
          <img
            src={getImageUrl(combo.image?.url)}
            alt={combo.name}
            className={cn(
              "w-full h-full object-cover",
              !combo.isAvailable && "grayscale"
            )}
          />
          {!combo.isAvailable && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-full">
              <span className="text-white text-xs font-medium px-2 py-0.5 bg-destructive rounded">
                Indisponible
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="w-full text-left space-y-0.5">
        {showSection && (
          <p className="text-xs text-muted-foreground font-medium">Combo</p>
        )}
        <p className="text-sm font-semibold truncate">
          {combo.name}
        </p>
      </div>

      <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2 mt-auto">
        <span className="text-sm font-semibold">
          {displayPrice(combo.price, currency)}
        </span>

        {combo.isAvailable && hasActiveOrder && (
          <div
            className={cn(
              "flex items-center gap-2 transition-opacity",
              quantity === 0 && "lg:opacity-0 lg:group-hover:opacity-100"
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => onUpdateQuantity(-1)}
              className="w-6 h-6 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:bg-muted transition-colors"
            >
              <Minus size={14} />
            </button>
            <span className="text-sm font-medium w-4 text-center">
              {quantity}
            </span>
            <button
              onClick={() => onUpdateQuantity(1)}
              className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center hover:bg-primary/90 transition-colors"
            >
              <Plus size={14} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- FormulaCard ----------

function FormulaCard({
  formula,
  currency,
  quantity,
  hasActiveOrder,
  showSection,
  onCardClick,
  onUpdateQuantity,
}: {
  formula: MenuFormula;
  currency: string;
  quantity: number;
  hasActiveOrder: boolean;
  showSection: boolean;
  onCardClick: () => void;
  onUpdateQuantity: (delta: number) => void;
}) {
  return (
    <div
      onClick={onCardClick}
      className={cn(
        "rounded-xl border bg-card p-3 flex flex-col items-center gap-3 transition-all cursor-pointer group relative",
        quantity > 0
          ? "border-primary shadow-sm"
          : "border-border hover:border-primary/30 hover:shadow-sm"
      )}
    >
      <div className="w-full bg-gray-50 rounded-lg flex items-center justify-center py-2 lg:py-4">
        <div className="w-24 h-24 rounded-full overflow-hidden relative">
          <img
            src={getImageUrl(formula.image?.url)}
            alt={formula.name}
            className="w-full h-full object-cover"
          />
        </div>
      </div>

      <div className="w-full text-left space-y-0.5">
        {showSection && (
          <p className="text-xs text-muted-foreground font-medium">Formule</p>
        )}
        <p className="text-sm font-semibold truncate">
          {formula.name}
        </p>
      </div>

      <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2 mt-auto">
        <span className="text-sm font-semibold">
          {displayPrice(formula.price, currency)}
        </span>

        {hasActiveOrder && (
          <div
            className={cn(
              "flex items-center gap-2 transition-opacity",
              quantity === 0 && "lg:opacity-0 lg:group-hover:opacity-100"
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => onUpdateQuantity(-1)}
              className="w-6 h-6 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:bg-muted transition-colors"
            >
              <Minus size={14} />
            </button>
            <span className="text-sm font-medium w-4 text-center">
              {quantity}
            </span>
            <button
              onClick={() => onUpdateQuantity(1)}
              className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center hover:bg-primary/90 transition-colors"
            >
              <Plus size={14} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- DailyDishCard ----------

function DailyDishCard({
  dish,
  currency,
  quantity,
  hasActiveOrder,
  showSection,
  onCardClick,
  onUpdateQuantity,
}: {
  dish: DailyDish;
  currency: string;
  quantity: number;
  hasActiveOrder: boolean;
  showSection: boolean;
  onCardClick: () => void;
  onUpdateQuantity: (delta: number) => void;
}) {
  return (
    <div
      onClick={onCardClick}
      className={cn(
        "rounded-xl border bg-card p-3 flex flex-col items-center gap-3 transition-all cursor-pointer group relative",
        quantity > 0
          ? "border-primary shadow-sm"
          : "border-border hover:border-primary/30 hover:shadow-sm"
      )}
    >
      <div className="w-full bg-gray-50 rounded-lg flex items-center justify-center py-2 lg:py-4">
        <img
          src={getImageUrl(dish.image?.url)}
          alt={dish.name}
          className="w-24 h-24 object-cover rounded-full"
        />
      </div>

      <div className="w-full text-left space-y-0.5">
        {showSection && (
          <p className="text-xs text-muted-foreground font-medium">
            Plat du jour
          </p>
        )}
        <p className="text-sm font-semibold truncate">
          {dish.name}
        </p>
      </div>

      <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2 mt-auto">
        <span className="text-sm font-semibold">
          {displayPrice(dish.price, currency)}
        </span>

        {hasActiveOrder && (
          <div
            className={cn(
              "flex items-center gap-2 transition-opacity",
              quantity === 0 && "lg:opacity-0 lg:group-hover:opacity-100"
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => onUpdateQuantity(-1)}
              className="w-6 h-6 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:bg-muted transition-colors"
            >
              <Minus size={14} />
            </button>
            <span className="text-sm font-medium w-4 text-center">
              {quantity}
            </span>
            <button
              onClick={() => onUpdateQuantity(1)}
              className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center hover:bg-primary/90 transition-colors"
            >
              <Plus size={14} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const ProtectedPosPage = () => (
  <RoleProtectedPage
    allowedRoles={[Roles.manager, Roles.owner, Roles.waiter, Roles.cashier]}
    allowedPermissions={POS_PERMISSIONS}
  >
    <PosPage />
  </RoleProtectedPage>
);

export default ProtectedPosPage;
